import { UrlTree } from '@angular/router';
import { EMPTY, Observable } from 'rxjs';
import { CreateAccount } from 'src/app/accounts/interfaces/accounts.types';
import { RefResponse } from 'src/app/nipt/interfaces/ref-response.interface';
import { LaboratoryName } from 'src/app/onboarding/lab/enums/lab.enum';
import {
  OnboardingIntentStep,
  OnboardingType,
} from 'src/app/shared/enums/intent.enum';
import { OnboardingPaths } from 'src/app/shared/interfaces/intent.interface';

export interface IntentStrategyConfig {
  type?: OnboardingType;
  steps?: OnboardingPaths;
  [OnboardingIntentStep.Welcome]?: {
    title: string;
    description: string;
  };
  [OnboardingIntentStep.Caution]: {
    paragraph1: string;
    paragraph2: string;
  };
  [OnboardingIntentStep.Complete]?: {
    title: string;
    description: string;
  };
}

/*
  Namming - on|NameOfScreen|ActionName OR on|ActionName
  Example - onWelcomeLogin OR onCustomerExists
*/
export abstract class IntentStrategy {
  abstract getConfig(): IntentStrategyConfig;
  abstract onAppInit?(milestone: OnboardingIntentStep): void;
  abstract onWelcomeInit?(forceInit?: boolean): Observable<any>;
  abstract onWelcomeLogin?(): void;
  abstract onWelcomeCreateAccount?(): void;
  abstract onRegisterCreateAccount(account: CreateAccount): Observable<any>;
  abstract onRegisterLogin(): void;
  abstract onNewsletterNextStep(): void;
  abstract onCautionNextStep(): void;
  abstract onNotValidatedEmail?(): Observable<UrlTree | boolean>;
  abstract onVerifyGoToLogin(): void;
  abstract onDownloadAfterCheckSkipClick(): void;
  abstract onAlreadyActivitedEmailGoToLogin(): void;
  abstract onLoginSuccessfully(returnUrl?: string): void;
  abstract onLoginRegister(): void;
  abstract onCreateCustomerSuccessfully(): void;
  abstract onTrackSkipClick?(): void;
  abstract onCompleteCloseClick?(): void;
  abstract onLabClaimNextClick?(lab: LaboratoryName): void;
  abstract onLabClaimCancelIntent?(): Observable<unknown>;
  abstract onKitClaimClaimClick?(
    isEU: string,
    collector: string
  ): Observable<any>;
  abstract onKitConsentAgree?(): void;
  abstract onKitConsentDisagree?(): void;
  abstract onKitSampleSampleTaken?(): void;
  abstract onNiptClaimInit?(): Observable<RefResponse>;
  abstract onNiptClaimActiveClick?(barcode: string): Observable<any>;
  abstract onEmailActivation(): Observable<UrlTree | boolean>;
  abstract onSkipEmailActivation(): Observable<UrlTree | boolean>;
  abstract onUserNotExists(): Observable<UrlTree | boolean>;
  abstract onCustomerNotExists(): Observable<UrlTree | boolean>;
  abstract onCustomerExists(): Observable<UrlTree | boolean>;
  abstract onCustomerWelcomeExists?(): Observable<UrlTree | boolean>;
  abstract onFallback?(): void;
}

export class IntentStrategyContext {
  public strategy: IntentStrategy;

  constructor(strategy: IntentStrategy) {
    this.strategy = strategy;
  }

  public getConfig(): IntentStrategyConfig {
    return this.strategy.getConfig();
  }

  public onAppInit(milestone: OnboardingIntentStep): void {
    if (this.strategy.onAppInit) {
      this.strategy.onAppInit(milestone);
    }
  }

  public onWelcomeInit(forceInit?: boolean): Observable<any> {
    if (this.strategy.onWelcomeInit) {
      return this.strategy.onWelcomeInit(forceInit);
    }
    return EMPTY;
  }

  public onWelcomeLogin(): void {
    if (this.strategy.onWelcomeLogin) {
      this.strategy.onWelcomeLogin();
    }
  }

  public onWelcomeCreateAccount(): void {
    if (this.strategy.onWelcomeCreateAccount) {
      this.strategy.onWelcomeCreateAccount();
    }
  }

  public onRegisterCreateAccount(props: CreateAccount): Observable<any> {
    return this.strategy.onRegisterCreateAccount(props);
  }

  public onRegisterLogin(): void {
    this.strategy.onRegisterLogin();
  }

  public onNewsletterNextStep(): void {
    this.strategy.onNewsletterNextStep();
  }

  public onCautionNextStep(): void {
    this.strategy.onCautionNextStep();
  }

  public onNotValidatedEmail(): Observable<UrlTree | boolean> {
    if (this.strategy.onNotValidatedEmail) {
      return this.strategy.onNotValidatedEmail();
    }
    return EMPTY;
  }

  public onVerifyGoToLogin(): void {
    this.strategy.onVerifyGoToLogin();
  }

  public onEmailActivation(): Observable<any> {
    return this.strategy.onEmailActivation();
  }

  public onSkipEmailActivation(): Observable<UrlTree | boolean> {
    return this.strategy.onSkipEmailActivation();
  }

  public onAlreadyActivitedEmailGoToLogin(): void {
    this.strategy.onAlreadyActivitedEmailGoToLogin();
  }

  public onDownloadAfterCheckSkipClick(): void {
    this.strategy.onDownloadAfterCheckSkipClick();
  }

  public onLoginSuccessfully(returnUrl: string): void {
    this.strategy.onLoginSuccessfully(returnUrl);
  }

  public onLoginRegister(): void {
    this.strategy.onLoginRegister();
  }

  public onCreateCustomerSuccessfully(): void {
    this.strategy.onCreateCustomerSuccessfully();
  }

  public onTrackSkipClick(): void {
    if (this.strategy.onTrackSkipClick) {
      this.strategy.onTrackSkipClick();
    }
  }

  public onLabClaimNextClick(lab: LaboratoryName) {
    if (this.strategy.onLabClaimNextClick) {
      this.strategy.onLabClaimNextClick(lab);
    }
  }

  public onNiptClaimInit(): Observable<RefResponse> {
    if (this.strategy.onNiptClaimInit) {
      return this.strategy.onNiptClaimInit();
    }
    return EMPTY;
  }

  public onNiptClaimActiveClick(barcode: string): Observable<unknown> {
    if (this.strategy.onNiptClaimActiveClick) {
      return this.strategy.onNiptClaimActiveClick(barcode);
    }
    return EMPTY;
  }

  public onLabClaimCancelIntent(): Observable<unknown> {
    if (this.strategy.onLabClaimCancelIntent) {
      return this.strategy.onLabClaimCancelIntent();
    }
    return EMPTY;
  }

  public onKitClaimClaimClick(
    isEU: string,
    collector: string
  ): Observable<any> {
    if (this.strategy.onKitClaimClaimClick) {
      return this.strategy.onKitClaimClaimClick(isEU, collector);
    }
    return EMPTY;
  }

  public onKitConsentAgree(): void {
    if (this.strategy.onKitConsentAgree) {
      this.strategy.onKitConsentAgree();
    }
  }

  public onKitConsentDisagree(): void {
    if (this.strategy.onKitConsentDisagree) {
      this.strategy.onKitConsentDisagree();
    }
  }

  public onKitSampleSampleTaken(): void {
    if (this.strategy.onKitSampleSampleTaken) {
      this.strategy.onKitSampleSampleTaken();
    } else if (this.strategy.onFallback) {
      this.strategy.onFallback();
    }
  }

  public onCompleteCloseClick() {
    if (this.strategy.onCompleteCloseClick) {
      this.strategy.onCompleteCloseClick();
    } else if (this.strategy.onFallback) {
      this.strategy.onFallback();
    }
  }

  public onCustomerNotExists(): Observable<any> {
    return this.strategy.onCustomerNotExists();
  }

  public onCustomerExists(): Observable<any> {
    return this.strategy.onCustomerExists();
  }

  public onCustomerWelcomeExists(): Observable<any> {
    if (this.strategy.onCustomerWelcomeExists) {
      return this.strategy.onCustomerWelcomeExists();
    }
    return EMPTY;
  }

  public onUserNotExists(): Observable<any> {
    return this.strategy.onUserNotExists();
  }
}
